import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ACCEPTED_LANGUAGES } from '../../config/commonConfig'
import {
    checkToken,
    requestRecoverLink,
    requestSetCredentials as APIRequestSetCredentials,
    getSettings
} from '../api/login'
import {
    REQUEST_TEAM_IDS,
    REQUEST_LOGIN,
    REQUEST_PWD_CHANGE,
    SET_LANGUAGE,
    SUCCEED_REQUEST_TOKEN
} from '../store/actionTypes'
import LoginLayout from './Layout'
import HeliocorStorage from '../../config/HeliocorStorage'
import isEmpty from 'lodash/isEmpty'
import { OPEN_NOTIFICATION } from "../../notifications/actionTypes"
import { GET_COMMON_SETTINGS } from '../../apps/common/store/actionTypes'
import contextTypes from '../../apps/dokstor/store/contextTypes.enum'
import { CLEAR_STORE } from '../../store/actionTypes'
import { GET_ORG_TEAMS } from '../../apps/Admin/store/actionTypes'
import { REQUEST_GET_DASHBOARDS_CONFIG } from '../../apps/dokstor/store/actionTypes'

const LoginContainer = () => {
    const [loginStatus, setLoginStatus] = useState(null)
    const [loginSubstatus, setLoginSubstatus] = useState(null)
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [oldpwd, setOldpwd] = useState('')
    const [newpwd1, setNewpwd1] = useState('')
    const [newpwd2, setNewpwd2] = useState('')
    const [recoverToken, setRecoverToken] = useState(null)
    const [error, setError] = useState(null)
    const [resetPwdRequested, setResetPwdRequested] = useState(false)
    const [recover, setRecover] = useState(false)
    const [supportEmail, setSupportEmail] = useState('')
    const [settingsLoaded, setSettingsLoaded] = useState(false)
    const [initializing, setInitializing] = useState(true)

    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const token = useSelector(state => state.auth.token)
    const loginError = useSelector(state => state.auth.loginError)
    const passwordExpired = useSelector(state => state.auth.passwordExpired)
    const permissions = useSelector(state => state.auth.permissions)
    const isSysAdmin = useSelector(state => state.auth.permissions?.SYSTEM?.includes('ADMIN'))
    const apiStatus = useSelector(state => state.common.apiStatus)
    const formatters = useSelector(state => state.common.formatters)

    useEffect(() => {
        const initializeLoginStatus = async () => {
            const { state, search } = location

            if (state?.changePwd) {
                setLoginStatus('changePwd')
                setResetPwdRequested(true)
                history.replace({ state: { resetPwdRequested: false } })
            }

            const tokenFromUrl = search.split('?token=')[1]
            if (tokenFromUrl) {
                setLoginStatus('credentials')
                setLoginSubstatus('checking')
                setRecoverToken(tokenFromUrl)
                const res = await checkToken(tokenFromUrl)
                const hasErrors = !!res?.errors
                setLoginSubstatus(hasErrors ? 'error' : 'success')
            }

            const loginData = await HeliocorStorage.getItem('loginData')
            const sidebar = await HeliocorStorage.getItem('sideBar')
            if (loginData) {
                dispatch({ type: SUCCEED_REQUEST_TOKEN, ...loginData, sidebar })
            } else {
                setLoginStatus('login')
            }

            const navigatorLanguage = window.navigator.language
            const storedLanguage = await HeliocorStorage.getItem('language')
            const fallbackLanguage = ACCEPTED_LANGUAGES.includes(navigatorLanguage) ? navigatorLanguage : 'en-EN'
            dispatch({ type: SET_LANGUAGE, language: storedLanguage || fallbackLanguage })
            i18n.changeLanguage(storedLanguage || fallbackLanguage)

            setInitializing(false)
        }
        initializeLoginStatus()
    }, [location, history, dispatch, i18n])

    useEffect(() => {
        document.title = `${t('button.login')} - Heliocor Platform`

        if (loginStatus === 'login' && !settingsLoaded) {
            setSettingsLoaded(true)
            getSettings().then(res => {
                setRecover(res?.recover)
                setSupportEmail(res?.supportEmail)
            })
        }
    }, [loginStatus, settingsLoaded, t])

    useEffect(() => {
        if (!isEmpty(permissions)) {
            const loginData = HeliocorStorage.getItem('loginData')

            if (loginData) {
                if (isEmpty(formatters) && !isSysAdmin) {
                    dispatch({ type: GET_COMMON_SETTINGS })
                }
                dispatch({ type: REQUEST_TEAM_IDS })
                dispatch({ type: GET_ORG_TEAMS })
                dispatch({ type: REQUEST_GET_DASHBOARDS_CONFIG })
            }

            if (passwordExpired) {
                setLoginStatus('changePwd')
            }
        }
    }, [permissions, dispatch])

    const handleInputChange = useCallback((key, value) => {
        if (key === 'username') setUsername(value.trim())
        else if (key === 'password') setPassword(value.trim())
        else if (key === 'oldpwd') setOldpwd(value.trim())
        else if (key === 'newpwd1') setNewpwd1(value.trim())
        else if (key === 'newpwd2') setNewpwd2(value.trim())
    }, [])

    const validatePasswords = () => {
        const validated = newpwd1 === newpwd2
        setError(!validated ? t("login.errorEqual") : null)
        return validated
    }

    const login = () => {
        const username = document.getElementById('username').value
        const password = document.getElementById('password').value

        dispatch({ type: REQUEST_LOGIN, username, password })
    }

    const requestPasswordRecover = () => {

        setLoginSubstatus('loading')
        const username = document.getElementById('username').value
        
        requestRecoverLink(username).then(res => {
            if (res?.errors) {
                res.errors.forEach(error => dispatch({ type: OPEN_NOTIFICATION, data: { message: t('notification.' + error.code), type: 'error' } }))
            } else {
                setLoginSubstatus('username_sent')
                dispatch({ type: OPEN_NOTIFICATION, data: { message: t('notification.SUCCESS_RECOVERY_EMAIL'), type: 'success' } })
            }
        })
    }

    const requestSetCredentials = () => {
        if (newpwd1 === newpwd2) {
            APIRequestSetCredentials(recoverToken, newpwd2).then(res => {
                if (res?.errors) {
                    res.errors.forEach(error => dispatch({ type: OPEN_NOTIFICATION, data: { message: t('notification.' + error.code), type: 'error' } }))
                } else {
                    setLoginSubstatus('setSuccess')
                }
            })
        }
    }

    const resetPwd = () => {
        if (validatePasswords()) {
            dispatch({ type: REQUEST_PWD_CHANGE, oldPassword: oldpwd, newPassword: newpwd1 })
        }
    }

    const redirectTo = status => setLoginStatus(status)

    const goToLogin = () => {
        HeliocorStorage.clear()
        dispatch({ type: CLEAR_STORE })
        setLoginStatus('login')
        setResetPwdRequested(false)
        history.push('/')
    }

    const goToHome = () => history.push('/home')
    const goToLastPage = () => history.goBack()

    let { from } = location.state || { from: { pathname: '/home' } }

    isSysAdmin && !location.state?.changePwd && (from = '/sysadmin')


    return initializing
        ? null
        : token && !passwordExpired && !resetPwdRequested && !loginError
            ? <Redirect to={from} />
            : (
                <LoginLayout
                    error={error}
                    goToHome={goToHome}
                    goToLastPage={goToLastPage}
                    goToLogin={goToLogin}
                    handleInputChange={handleInputChange}
                    loginError={loginError}
                    login={login}
                    loginStatus={loginStatus}
                    loginSubstatus={loginSubstatus}
                    onPwdChange={resetPwd}
                    passwordExpired={passwordExpired}
                    redirectTo={redirectTo}
                    requestPasswordRecover={requestPasswordRecover}
                    requestSetCredentials={requestSetCredentials}
                    resetPwd={resetPwd}
                    showRecoverPassword={() => setLoginStatus('recoverPassword')}
                    username={username}
                    password={password}
                    loginLoading={apiStatus[contextTypes.LOGIN]}
                    resetFilled={!!(oldpwd && newpwd1 && newpwd2)}
                    recoverEnabled={recover}
                    supportEmail={supportEmail}
                />
            )
}

export default LoginContainer
